import { DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { openLocalFile } from "@/utils/openDocument";
import { Button, Form, FormInstance, Input, message, Upload, UploadFile } from "antd";

export interface CustomDocumentEntry {
  key: string;
  name: string;
  file: UploadFile[];
}

/** Saved document ids that the user removed while editing (their row is no longer in the list). */
export const getRemovedCustomDocumentIds = (
  existing: { id: string }[] | undefined,
  current: CustomDocumentEntry[],
): string[] => (existing || []).map((d) => d.id).filter((id) => !current.some((d) => d.key === id));

interface CustomDocumentUploadListProps {
  form: FormInstance;
  fieldName: string;
  value: CustomDocumentEntry[];
  onChange: (docs: CustomDocumentEntry[]) => void;
  /** Called with the saved document's id when the user clicks an already-saved file name. */
  onPreviewExisting?: (docId: string) => void;
}

/**
 * A dynamic, user-managed list of "name a document, then upload it" rows — used when a
 * vendor/client's country is neither India nor the United States, so there's no fixed set
 * of document types to collect. At least one complete row (name + file) is required, enforced
 * via a hidden Form.Item so it participates in the surrounding form's validation.
 */
export default function CustomDocumentUploadList({ form, fieldName, value, onChange, onPreviewExisting }: CustomDocumentUploadListProps) {
  const addRow = () => {
    onChange([...value, { key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name: "", file: [] }]);
  };

  const removeRow = (key: string) => {
    const next = value.filter((doc) => doc.key !== key);
    onChange(next);
    form.setFieldsValue({ [fieldName]: next });
  };

  const updateRow = (key: string, patch: Partial<CustomDocumentEntry>) => {
    const next = value.map((doc) => (doc.key === key ? { ...doc, ...patch } : doc));
    onChange(next);
    form.setFieldsValue({ [fieldName]: next });
  };

  return (
    <div className="flex flex-col pb-4" data-doc-field={fieldName}>
      <label className="text-sm mb-2">
        Custom Documents
        <span className="text-red-500 ml-0.5">*</span>
      </label>

      <div className="flex flex-col gap-2">
        {value.map((doc) => {
          const uploadedFile = doc.file[0];
          return (
            <div key={doc.key} className="flex items-start gap-3 rounded-md border border-gray-200 p-3">
              <Input
                placeholder="Document name (e.g. Business License)"
                value={doc.name}
                onChange={(e) => updateRow(doc.key, { name: e.target.value })}
                className="flex-1"
              />
              <div className="flex flex-col items-start gap-1 w-40 shrink-0">
                <Upload
                  fileList={doc.file}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const isValidType = ["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(file.type);
                    if (!isValidType) {
                      message.error("You can only upload PDF, JPG, or PNG files!");
                      return Upload.LIST_IGNORE;
                    }
                    const isValidSize = file.size / 1024 / 1024 < 5;
                    if (!isValidSize) {
                      message.error("File must be smaller than 5MB!");
                      return Upload.LIST_IGNORE;
                    }
                    updateRow(doc.key, {
                      file: [{ uid: file.uid || "-1", name: file.name, status: "done", originFileObj: file } as UploadFile],
                    });
                    return false;
                  }}
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>{uploadedFile ? "Replace" : "Upload"}</Button>
                </Upload>
                {uploadedFile && (
                  <a
                    role="button"
                    className="text-xs text-blue-600 hover:underline cursor-pointer truncate max-w-[10rem] leading-tight"
                    title={`${uploadedFile.name} (click to open)`}
                    onClick={() => (uploadedFile.originFileObj ? openLocalFile(uploadedFile) : onPreviewExisting?.(doc.key))}
                  >
                    {uploadedFile.name}
                  </a>
                )}
              </div>
              {/* Trash hidden for already-saved documents until the delete behaviour is decided. */}
              {!uploadedFile?.uid?.startsWith("-existing-") && (
                <Button danger icon={<DeleteOutlined />} onClick={() => removeRow(doc.key)} />
              )}
            </div>
          );
        })}
      </div>

      <Button type="dashed" icon={<PlusOutlined />} className="mt-3 w-full" onClick={addRow}>
        Add Document
      </Button>

      <Form.Item
        name={fieldName}
        hidden
        rules={[
          {
            validator: async (_, fieldValue) => {
              const docs: CustomDocumentEntry[] = Array.isArray(fieldValue) ? fieldValue : value;
              if (docs.length === 0) {
                throw new Error("At least one document is required");
              }
              if (docs.some((doc) => !doc.name.trim() || doc.file.length === 0)) {
                throw new Error("Every document needs both a name and an uploaded file");
              }
            },
          },
        ]}
      >
        <Input />
      </Form.Item>
    </div>
  );
}
